import { escapeForRolodexIlike } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/browseAllFilters'
import type { DocumentPageMetadataUserEditEntry } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import { bareDocumentPageId } from 'features/knowledgeDocumentViewer/utils/cantileverHostIds'
import type { SystemDocumentPageLoadOptions } from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import {
  effectivePageRevisionFamilyKey,
  normalizeRevisionFamilyKeyPart,
  pageRevisionFamilyKey,
} from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'
import {
  pageMetadataUserEditPageNum,
  selectLatestConfirmedPageMetadataUserEdit,
} from 'features/knowledgeDocumentViewer/utils/selectLatestConfirmedPageMetadataUserEdit'
import { RolodexEntryType, RolodexFilterOp } from 'types'

export function revisionFamilyPageCandidatesByDrawingNumber(
  drawingNumber: string,
  scopedTags: SystemDocumentPageLoadOptions['tags']
): SystemDocumentPageLoadOptions | undefined {
  const drawing = drawingNumber.trim()
  if (!drawing) return undefined
  // Rolodex '~' is ILIKE '%value%': fetch case-insensitive candidates,
  // then enforce exact normalized family membership during resolution.
  return {
    type: [RolodexEntryType.SYSTEM_DOCUMENT_PAGE],
    tags: scopedTags,
    filters: [
      {
        field: 'data',
        key: 'issuer_drawing_number',
        op: RolodexFilterOp.REGEX,
        value: escapeForRolodexIlike(drawing),
      },
    ],
    limit: 1000,
  }
}

export function latestRevisionFamilyEditsByPageId(
  edits: readonly DocumentPageMetadataUserEditEntry[],
  currentEdit?: DocumentPageMetadataUserEditEntry
): Map<string, DocumentPageMetadataUserEditEntry> {
  const byPage = new Map<string, DocumentPageMetadataUserEditEntry>()
  for (const edit of edits) {
    const pageNum = pageMetadataUserEditPageNum(edit)
    if (pageNum === undefined) continue
    const pageId = bareDocumentPageId(edit.entry.data.document_id, pageNum)
    const prior = byPage.get(pageId)
    const latest = selectLatestConfirmedPageMetadataUserEdit(
      prior ? [prior, edit] : [edit]
    )
    if (latest) byPage.set(pageId, latest)
  }
  // The per-page cache is seeded on save before the site history revalidates.
  const current =
    currentEdit && selectLatestConfirmedPageMetadataUserEdit([currentEdit])
  const pageNum = current && pageMetadataUserEditPageNum(current)
  if (current && pageNum !== undefined) {
    byPage.set(
      bareDocumentPageId(current.entry.data.document_id, pageNum),
      current
    )
  }
  return byPage
}

export function editedPageIdsForDrawingNumber(
  editsByPageId: ReadonlyMap<string, DocumentPageMetadataUserEditEntry>,
  drawingNumber: string | null | undefined
): string[] {
  const drawing = normalizeRevisionFamilyKeyPart(drawingNumber)
  if (!drawing) return []
  return [...editsByPageId]
    .filter(
      ([, edit]) =>
        normalizeRevisionFamilyKeyPart(
          edit.entry.data.values?.issuer_drawing_number
        ) === drawing
    )
    .map(([pageId]) => pageId)
    .sort()
}

export function revisionFamilyEditForPage(
  page: SystemDocumentPageEntry,
  editsByPageId: ReadonlyMap<string, DocumentPageMetadataUserEditEntry>
): DocumentPageMetadataUserEditEntry | undefined {
  const { document_id, page_num } = page.entry.data
  if (!document_id || page_num === undefined) return undefined
  return editsByPageId.get(bareDocumentPageId(document_id, page_num))
}

export function pageMatchesRevisionFamilyScope(
  page: SystemDocumentPageEntry,
  scopedTags: Readonly<Record<string, string | string[]>>
): boolean {
  return Object.entries(scopedTags).every(([tag, expected]) => {
    const actual = page.entry.tags?.[tag]
    return Array.isArray(expected)
      ? actual !== undefined && actual !== null && expected.includes(actual)
      : actual === expected
  })
}

export function resolvePageRevisionFamilyCandidates({
  pages,
  currentFamilyKey,
  editsByPageId,
  scopedTags,
}: {
  pages: readonly SystemDocumentPageEntry[]
  currentFamilyKey: string | undefined
  editsByPageId: ReadonlyMap<string, DocumentPageMetadataUserEditEntry>
  scopedTags: Readonly<Record<string, string | string[]>>
}): SystemDocumentPageEntry[] {
  if (!currentFamilyKey) return []
  const byId = new Map<string, SystemDocumentPageEntry>()
  for (const page of pages) {
    if (!pageMatchesRevisionFamilyScope(page, scopedTags)) continue
    const generatedKey = pageRevisionFamilyKey(
      page.entry.data.issuer_drawing_number,
      page.entry.data.sheet_number
    )
    const effectiveKey = effectivePageRevisionFamilyKey(
      page,
      revisionFamilyEditForPage(page, editsByPageId)?.entry.data.values ??
        undefined
    )
    // Original siblings remain available to canonicalize stored comparison data.
    if (
      generatedKey === currentFamilyKey ||
      effectiveKey === currentFamilyKey
    ) {
      byId.set(page.entry.id, page)
    }
  }
  return [...byId.values()]
}
